<template>
  <div v-if="isCartOpen" class="cart-modal-overlay" @click.self="toggleCart">
    <div class="cart-modal">
      <header class="modal-header">
        <h2>Your Cart</h2>
        <button class="close-button" @click="toggleCart">
          <fa :icon="['fas', 'times']" />
        </button>
      </header>

      <div class="cart-body">
        <div v-if="cartItems.length" class="cart-items-scroll">
          <div class="cart-item" v-for="(item, index) in cartItems" :key="index">
            <div class="cart-details">
              <p><strong>Recipient:</strong><span class="cart-value">{{ item.recipientFirstName }} {{ item.recipientLastName }}</span></p>
              <p><strong>Tone:</strong><span class="cart-value">{{ item.tone }}</span></p>
              <p><strong>Style:</strong><span class="cart-value">{{ item.style }}</span></p>
              <button @click="toggleMoreInfo(index)">More Info</button>
              <div v-if="expandedIndex === index" class="more-info">
                <p><strong>From:</strong><span class="cart-value">{{ item.fromField }}</span></p>
                <p><strong>Address:</strong><span class="cart-value">{{ item.address.street }}, {{ item.address.city }}, {{ item.address.state }} {{ item.address.postalCode }}</span></p>
                <p><strong>Additional Info:</strong><span class="cart-value">{{ item.userIntent?.trim() || "N/A" }}</span></p>
                <p><strong>Occasion:</strong><span class="cart-value">{{ item.occasion?.trim() || "N/A" }}</span></p>
              </div>
            </div>
            <div class="cart-item-actions">
              <fa :icon="['fas', 'trash']" class="trash-icon" @click="confirmRemove(index)" />
            </div>
          </div>
        </div>
        <div v-else class="empty-cart">
          <fa :icon="['fas', 'shopping-cart']" class="empty-cart-icon" />
          <p>Your cart is empty.</p>
          <button @click="goToCreate" class="cta-button secondary">Create a Postcard</button>
        </div>
        <div v-if="cartItems.length" class="cart-footer">
          <button @click="proceedToCheckout" class="cta-button">Proceed to Checkout</button>
        </div>
      </div>

      <ConfirmationModal v-if="showConfirmation" @confirm="removeItem" @close="cancelRemove" />
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ConfirmationModal from './ConfirmationModal.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: { fa: FontAwesomeIcon, ConfirmationModal },
  data() {
    return {
      showConfirmation: false,
      itemToRemove: null,
      expandedIndex: null,
    };
  },
  computed: {
    ...mapGetters(['cartItems', 'isCartOpen']),
  },
  methods: {
    ...mapActions(['removeFromCart', 'toggleCart']),
    confirmRemove(index) {
      this.itemToRemove = index;
      this.showConfirmation = true;
    },
    toggleMoreInfo(index) {
      this.expandedIndex = this.expandedIndex === index ? null : index;
    },
    removeItem() {
      if (this.itemToRemove !== null) {
        this.removeFromCart(this.itemToRemove);
      }
      this.showConfirmation = false;
      this.itemToRemove = null;
    },
    cancelRemove() {
      this.showConfirmation = false;
      this.itemToRemove = null;
    },
    proceedToCheckout() {
      this.toggleCart();
      this.$router.push('/checkout');
    },
    goToCreate() {
      this.toggleCart();
      this.$router.push('/create-postcard');
    }
  }
};
</script>

<style scoped>
.cart-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.cart-modal {
  background-color: #ffffff;
  padding: 0;
  border-radius: 12px;
  max-width: 520px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  position: relative;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  padding: 16px 24px;
}

.modal-header h2 {
  font-size: 1.5rem;
  margin: 0;
  color: var(--hollywood-cerise);
}

.close-button {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: var(--hollywood-cerise);
  cursor: pointer;
}

.cart-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.cart-items-scroll {
  overflow-y: auto;
  padding: 20px 24px;
  flex-grow: 1;
}

.cart-footer {
  border-top: 1px solid #eee;
  padding: 16px 24px;
  background-color: #fff;
}

.cart-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.cart-details {
  flex: 1;
  padding: 0 15px;
}

.cart-value {
  color: var(--violet-lush);
  margin-left: 6px;
}

.trash-icon {
  color: var(--hollywood-cerise);
  font-size: 1.2rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.trash-icon:hover {
  color: var(--violet-lush);
}

.more-info {
  margin-top: 10px;
  background-color: var(--light-pink);
  padding: 10px;
  border-radius: 6px;
  font-size: 0.95rem;
  color: var(--rich-black);
}

.cart-modal > div:last-of-type p {
  text-align: center;
  color: var(--rich-black);
  font-weight: 500;
}

.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
}

.empty-cart-icon {
  font-size: 3rem;
  color: var(--powder-blue);
  margin-bottom: 16px;
}

.empty-cart p {
  color: var(--rich-black);
  font-weight: 500;
  margin-bottom: 20px;
}

.cta-button.secondary {
  background-color: var(--violet-lush);
}

.cta-button.secondary:hover {
  background-color: var(--hollywood-cerise);
}

@media (max-width: 480px) {
  .cart-modal {
    max-width: 95%;
  }
  .cart-items-scroll {
    padding: 16px;
  }
  .cart-details {
    padding: 0 10px;
  }
  .more-info {
    font-size: 0.9rem;
  }
}
</style>
